<template>
  <div class="rto-history">
    <!-- Header -->
    <header class="rto-history-header">
      <div class="rto-history-title-row">
        <b-icon icon="clock-history" class="rto-history-title-icon" />
        <h2 class="rto-history-title">Histórico de Otimizações RTO</h2>
        <span v-if="!loading && filteredItems.length > 0" class="rto-history-count">{{ filteredItems.length }}</span>
      </div>
      <div class="rto-history-actions">
        <div class="rto-history-filter">
          <label class="rto-history-filter-label">Tipo</label>
          <select v-model="filterTipo" @change="load" class="rto-history-select">
            <option :value="null">Todos</option>
            <option :value="0">Real</option>
            <option :value="1">Teste</option>
            <option :value="2">Otimização</option>
          </select>
        </div>
        <div class="rto-history-filter">
          <label class="rto-history-filter-label">Otimização</label>
          <select v-model="filterOtimizacao" class="rto-history-select">
            <option :value="null">Todas</option>
            <option v-for="o in otimizacoesList" :key="o.id" :value="o.id">
              {{ o.nome }} (#{{ o.id }})
            </option>
          </select>
        </div>
        <button class="rto-history-refresh-btn" :disabled="loading" @click="load">
          <b-spinner v-if="loading" small class="mr-1" />
          <b-icon v-else icon="arrow-clockwise" class="mr-1" />
          {{ loading ? 'Carregando...' : 'Atualizar' }}
        </button>
        <button
          v-if="runningCount > 0"
          class="rto-history-cancel-all-btn"
          :disabled="cancelAllInFlight"
          @click="cancelAllRunning"
          :title="`Cancelar todas as ${runningCount} otimização(ões) em execução`"
        >
          <b-spinner v-if="cancelAllInFlight" small class="mr-1" />
          <b-icon v-else icon="x-octagon-fill" class="mr-1" />
          Cancelar todas ({{ runningCount }})
        </button>
      </div>
    </header>

    <!-- Last optimization card (CA-07.5) -->
    <section v-if="lastItem && !error" class="rto-last-card" :class="lastCardAccentClass">
      <div class="rto-last-card-header">
        <span class="rto-last-card-eyebrow">Última Otimização</span>
        <span class="rto-history-chip" :class="chipStatusClass(lastItem.statusCodigo)">{{ formatStatus(lastItem.status) }}</span>
        <span class="rto-history-chip" :class="chipTipoClass(lastItem.tipoCodigo)">{{ formatTipo(lastItem.tipo) }}</span>
        <span class="rto-last-card-time">{{ formatRelativeTime(lastItem.dataFim || lastItem.dataInicio) }}</span>
        <button class="rto-last-card-details-btn" @click="openDetails(lastItem.id)">
          <b-icon icon="eye" class="mr-1" /> Ver detalhes completos
        </button>
      </div>

      <div class="rto-last-card-body">
        <!-- Identificação -->
        <div class="rto-last-card-col">
          <div class="rto-last-card-title">{{ lastItem.otimizacaoNome || '—' }}</div>
          <div v-if="parsedLastErro" class="rto-last-card-error">
            <div class="rto-last-card-error-head">
              <b-icon icon="exclamation-triangle-fill" class="mr-1 rto-last-card-error-icon" />
              <span class="rto-last-card-error-text">{{ lastErroResumo }}</span>
            </div>
            <button v-if="parsedLastErro.total" type="button" class="rto-last-card-error-count" @click="openDetails(lastItem.id)">
              {{ parsedLastErro.warnings }} aviso(s)<template v-if="parsedLastErro.erros"> · {{ parsedLastErro.erros }} erro(s)</template> — ver detalhes
            </button>
          </div>
        </div>

        <!-- Função Objetivo -->
        <div class="rto-last-card-col">
          <div class="rto-last-card-label">Função Objetivo</div>
          <div v-if="lastDetailsLoading" class="rto-last-card-loading">
            <b-spinner small />
          </div>
          <div v-else-if="lastDetails && lastDetails.funcaoObjetivo" class="rto-last-card-objective">
            <div class="mono rto-last-card-objective-tag">{{ lastDetails.funcaoObjetivo.tag }}</div>
            <div class="rto-last-card-objective-row">
              <span class="rto-last-card-label-inline">Ação:</span>
              <span class="rto-history-modo" :class="modoBadgeClass(lastItem)">{{ formatModoShort(lastItem) }}</span>
            </div>
            <div class="rto-last-card-objective-row">
              <span class="rto-last-card-label-inline">Simulado:</span>
              <span class="mono rto-last-card-objective-value">{{ formatNum(lastDetails.funcaoObjetivo.valorSimulado) }}</span>
            </div>
            <div class="rto-last-card-subtle rto-last-card-objective-row">
              <span class="mono">ID #{{ lastItem.id }}</span>
              <span v-if="lastItem.modo" class="rto-last-card-sep">·</span>
              <span v-if="lastItem.modo">{{ lastItem.modo }}</span>
              <span class="rto-last-card-sep">·</span>
              <span class="mono">{{ formatDuration(lastItem.duracaoMin) }}</span>
            </div>
            <div class="rto-last-card-objective-row">
              <span class="rto-last-card-label-inline">Range:</span>
              <span class="mono">{{ formatNum(lastDetails.funcaoObjetivo.minimo) }} – {{ formatNum(lastDetails.funcaoObjetivo.maximo) }}</span>
            </div>
          </div>
          <div v-else class="rto-last-card-subtle">—</div>
        </div>

        <!-- Resumo de manipuladas e restrições -->
        <div class="rto-last-card-col">
          <div class="rto-last-card-label">Resumo</div>
          <div v-if="lastDetailsLoading" class="rto-last-card-loading">
            <b-spinner small />
          </div>
          <div v-else-if="lastDetails" class="rto-last-card-resumo">
            <div class="rto-last-card-resumo-row">
              <span class="rto-last-card-label-inline">Manipuladas:</span>
              <span class="rto-history-chip rto-history-chip--neutro">{{ (lastDetails.variaveisManipuladas || []).length }}</span>
            </div>
            <div class="rto-last-card-resumo-row">
              <span class="rto-last-card-label-inline">Restrições:</span>
              <span v-if="lastDetails.resumo" class="rto-last-card-resumo-chips">
                <span class="rto-history-chip rto-history-chip--ok">{{ lastDetails.resumo.restricoesOk }} OK</span>
                <span v-if="lastDetails.resumo.restricoesViolando > 0" class="rto-history-chip rto-history-chip--atencao"><b-icon icon="exclamation-triangle-fill" class="mr-1" />{{ lastDetails.resumo.restricoesViolando }} aten.</span>
                <span v-if="lastDetails.resumo.restricoesSemDado > 0" class="rto-history-chip rto-history-chip--neutro">{{ lastDetails.resumo.restricoesSemDado }} s/dado</span>
              </span>
              <span v-else class="rto-history-chip rto-history-chip--neutro">{{ (lastDetails.restricoes || []).length }}</span>
            </div>
            <div v-if="lastDetails.tempos && lastDetails.tempos.iteracoes != null" class="rto-last-card-resumo-row">
              <span class="rto-last-card-label-inline">Iterações:</span>
              <span class="mono">{{ lastDetails.tempos.iteracoes }}</span>
            </div>
          </div>
          <div v-else-if="lastDetailsError" class="rto-last-card-subtle">
            <b-icon icon="exclamation-circle" /> Não foi possível carregar detalhes
          </div>
          <div v-else class="rto-last-card-subtle">—</div>
        </div>
      </div>
    </section>

    <!-- Tabs (CA-07.6) -->
    <nav v-if="!loading || items.length > 0" class="rto-history-tabs">
      <button
        class="rto-history-tab"
        :class="{ 'rto-history-tab--active': activeTab === 'todas' }"
        @click="activeTab = 'todas'"
      >
        Todas <span class="rto-history-tab-count">{{ tabCounts.todas }}</span>
      </button>
      <button
        class="rto-history-tab"
        :class="{ 'rto-history-tab--active': activeTab === 'publicadas' }"
        @click="activeTab = 'publicadas'"
      >
        Publicadas <span class="rto-history-tab-count rto-history-tab-count--ok">{{ tabCounts.publicadas }}</span>
      </button>
      <button
        class="rto-history-tab"
        :class="{ 'rto-history-tab--active': activeTab === 'comErro' }"
        @click="activeTab = 'comErro'"
      >
        Com Erro <span class="rto-history-tab-count rto-history-tab-count--erro">{{ tabCounts.comErro }}</span>
      </button>
    </nav>

    <!-- States -->
    <div v-if="loading && items.length === 0" class="rto-history-state">
      <b-spinner />
      <span>Carregando histórico...</span>
    </div>

    <div v-else-if="error" class="rto-history-state rto-history-state--error">
      <b-icon icon="exclamation-triangle" />
      <span>{{ error }}</span>
    </div>

    <div v-else-if="!loading && filteredItems.length === 0" class="rto-history-state">
      <b-icon icon="inbox" />
      <span>{{ emptyMessage }}</span>
    </div>

    <!-- Table -->
    <div v-else class="rto-history-table-wrap">
      <table class="rto-history-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Tipo</th>
            <th>Objetivo</th>
            <th>Modo</th>
            <th>Status</th>
            <th>Início</th>
            <th>Fim</th>
            <th>Duração</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in filteredItems" :key="row.id">
            <td class="rto-history-td-mono">{{ row.id }}</td>
            <td>
              <span class="rto-history-chip" :class="chipTipoClass(row.tipoCodigo)">{{ formatTipo(row.tipo) }}</span>
            </td>
            <td class="rto-history-td-truncate" :title="row.otimizacaoNome">{{ row.otimizacaoNome || '—' }}</td>
            <td><span class="rto-history-modo" :class="modoBadgeClass(row)">{{ formatModoShort(row) }}</span></td>
            <td>
              <span class="rto-history-chip" :class="chipStatusClass(row.statusCodigo)">{{ formatStatus(row.status) }}</span>
            </td>
            <td class="rto-history-td-mono">{{ formatDate(row.dataInicio) }}</td>
            <td class="rto-history-td-mono">{{ formatDate(row.dataFim) }}</td>
            <td class="rto-history-td-mono">{{ formatDuration(row.duracaoMin) }}</td>
            <td>
              <div class="rto-history-row-actions">
                <button class="rto-history-details-btn" @click="openDetails(row.id)">
                  <b-icon icon="eye" class="mr-1" /> Detalhes
                </button>
                <button
                  v-if="isRunning(row)"
                  class="rto-history-cancel-row-btn"
                  :disabled="cancelInFlightId === row.id"
                  @click="cancelOne(row.id)"
                  title="Cancelar esta otimização"
                >
                  <b-spinner v-if="cancelInFlightId === row.id" small />
                  <b-icon v-else icon="x-circle" />
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Details modal -->
    <b-modal
      v-model="showDetails"
      size="xl"
      :title="detailsTitle"
      hide-footer
      no-close-on-backdrop
      scrollable
      modal-class="rto-history-modal"
    >
      <div v-if="detailsLoading" class="rto-history-state">
        <b-spinner />
        <span>Carregando detalhes...</span>
      </div>

      <div v-else-if="detailsError" class="rto-history-state rto-history-state--error">
        <b-icon icon="exclamation-triangle" />
        <span>{{ detailsError }}</span>
      </div>

      <div v-else-if="details" class="rto-history-details">
        <!-- Identification -->
        <section class="rto-history-section">
          <h4 class="rto-history-section-title">Identificação</h4>
          <div class="rto-history-grid">
            <div><label>ID</label><span class="mono">{{ details.id }}</span></div>
            <div><label>Otimização</label><span>{{ details.otimizacaoNome || '—' }}</span></div>
            <div><label>Otimização ID</label><span class="mono">{{ details.otimizacaoId ?? '—' }}</span></div>
            <div><label>Tipo</label><span>{{ formatTipo(details.tipo) }}</span></div>
            <div>
              <label>Modo</label>
              <span class="rto-history-modo" :class="modoBadgeClass(details)" :title="details.modoOtimizacao">{{ formatModoShort(details) }}</span>
            </div>
            <div><label>Status</label><span>{{ formatStatus(details.status) }}</span></div>
          </div>
        </section>

        <!-- Diagnóstico / Erros e Avisos -->
        <section v-if="parsedErro || parsedErroRto" class="rto-history-section">
          <h4 class="rto-history-section-title">Diagnóstico</h4>
          <div class="rto-diag">
            <div v-if="parsedErro" class="rto-diag-block">
              <div class="rto-diag-label">Erro do processamento</div>
              <p v-if="parsedErro.header" class="rto-diag-header">{{ parsedErro.header }}</p>
              <button v-if="parsedErro.total" type="button" class="rto-diag-toggle" @click="showErroList = !showErroList">
                <b-icon :icon="showErroList ? 'chevron-down' : 'chevron-right'" />
                <span v-if="parsedErro.warnings">{{ parsedErro.warnings }} aviso(s)</span><span v-if="parsedErro.erros">{{ parsedErro.warnings ? ' · ' : '' }}{{ parsedErro.erros }} erro(s)</span>
                <span class="rto-diag-toggle-hint">{{ showErroList ? 'ocultar' : 'ver' }}</span>
              </button>
              <ul v-show="showErroList && parsedErro.total" class="rto-diag-list">
                <li v-for="(it, i) in parsedErro.items" :key="i" :class="['rto-diag-item', 'rto-diag-item--' + it.tipo]">
                  <b-icon :icon="it.tipo === 'erro' ? 'x-circle-fill' : 'exclamation-triangle-fill'" class="rto-diag-item-icon" />
                  <span>{{ it.texto }}</span>
                </li>
              </ul>
            </div>
            <div v-if="parsedErroRto" class="rto-diag-block">
              <div class="rto-diag-label">Erro RTO</div>
              <p v-if="parsedErroRto.header" class="rto-diag-header">{{ parsedErroRto.header }}</p>
              <button v-if="parsedErroRto.total" type="button" class="rto-diag-toggle" @click="showErroRtoList = !showErroRtoList">
                <b-icon :icon="showErroRtoList ? 'chevron-down' : 'chevron-right'" />
                <span v-if="parsedErroRto.warnings">{{ parsedErroRto.warnings }} aviso(s)</span><span v-if="parsedErroRto.erros">{{ parsedErroRto.warnings ? ' · ' : '' }}{{ parsedErroRto.erros }} erro(s)</span>
                <span class="rto-diag-toggle-hint">{{ showErroRtoList ? 'ocultar' : 'ver' }}</span>
              </button>
              <ul v-show="showErroRtoList && parsedErroRto.total" class="rto-diag-list">
                <li v-for="(it, i) in parsedErroRto.items" :key="i" :class="['rto-diag-item', 'rto-diag-item--' + it.tipo]">
                  <b-icon :icon="it.tipo === 'erro' ? 'x-circle-fill' : 'exclamation-triangle-fill'" class="rto-diag-item-icon" />
                  <span>{{ it.texto }}</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        <!-- Tempos -->
        <section class="rto-history-section">
          <h4 class="rto-history-section-title">Tempos</h4>
          <div class="rto-history-grid">
            <div><label>Início</label><span class="mono">{{ formatDate(details.tempos.inicio) }}</span></div>
            <div><label>Fim</label><span class="mono">{{ formatDate(details.tempos.fim) }}</span></div>
            <div><label>Duração</label><span class="mono">{{ formatDuration(details.tempos.elapsedTimeMin) }}</span></div>
            <div v-if="details.tempos.iteracoes != null"><label>Iterações</label><span class="mono">{{ details.tempos.iteracoes }}</span></div>
            <div v-if="details.tempos.optInfo"><label>Opt Info</label><span>{{ details.tempos.optInfo }}</span></div>
          </div>
        </section>

        <!-- Função Objetivo -->
        <section v-if="details.funcaoObjetivo" class="rto-history-section">
          <h4 class="rto-history-section-title">Função Objetivo</h4>
          <div class="rto-history-grid">
            <div v-if="details.funcaoObjetivo.label"><label>Apelido</label><span>{{ details.funcaoObjetivo.label }}</span></div>
            <div><label>Tag</label><span class="mono">{{ details.funcaoObjetivo.tag }}</span></div>
            <div><label>Mapeamento</label><span class="mono">{{ details.funcaoObjetivo.mapeamento }}</span></div>
            <div><label>Mínimo</label><span class="mono">{{ formatNum(details.funcaoObjetivo.minimo) }}</span></div>
            <div><label>Máximo</label><span class="mono">{{ formatNum(details.funcaoObjetivo.maximo) }}</span></div>
            <div><label>Valor Simulado</label><span class="mono">{{ formatNum(details.funcaoObjetivo.valorSimulado) }}</span></div>
            <div><label>Penalty Factor</label><span class="mono">{{ formatNum(details.funcaoObjetivo.penaltyFactor) }}</span></div>
          </div>
        </section>

        <!-- Variáveis Manipuladas -->
        <section v-if="details.variaveisManipuladas && details.variaveisManipuladas.length > 0" class="rto-history-section">
          <h4 class="rto-history-section-title">
            Variáveis Manipuladas
            <span class="rto-history-section-count">{{ details.variaveisManipuladas.length }}</span>
          </h4>
          <div class="rto-history-table-wrap rto-history-table-wrap--compact">
            <table class="rto-history-table">
              <thead>
                <tr>
                  <th>Apelido</th>
                  <th>Tag</th>
                  <th>Mínimo</th>
                  <th>Máximo</th>
                  <th>Inicial</th>
                  <th>Ótimo</th>
                  <th>Variação</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(v, i) in details.variaveisManipuladas" :key="i">
                  <td>{{ v.label || '—' }}</td>
                  <td class="mono">{{ v.tag }}</td>
                  <td class="mono">{{ formatNum(v.minimo) }}</td>
                  <td class="mono">{{ formatNum(v.maximo) }}</td>
                  <td class="mono">{{ formatNum(v.valorInicial) }}</td>
                  <td class="mono">{{ formatNum(v.valorOtimo) }}</td>
                  <td class="mono" :class="variationClass(v.valorInicial, v.valorOtimo)">
                    {{ formatVariation(v.valorInicial, v.valorOtimo) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <!-- Restrições -->
        <section v-if="details.restricoes && details.restricoes.length > 0" class="rto-history-section">
          <h4 class="rto-history-section-title">
            Restrições
            <span class="rto-history-section-count">{{ details.restricoes.length }}</span>
            <span v-if="details.resumo" class="rto-history-resumo">
              <span class="rto-history-chip rto-history-chip--ok">{{ details.resumo.restricoesOk }} OK</span>
              <span v-if="details.resumo.restricoesViolando > 0" class="rto-history-chip rto-history-chip--atencao"><b-icon icon="exclamation-triangle-fill" class="mr-1" />{{ details.resumo.restricoesViolando }} em atenção</span>
              <span v-if="details.resumo.restricoesSemDado > 0" class="rto-history-chip rto-history-chip--neutro">{{ details.resumo.restricoesSemDado }} sem dado</span>
            </span>
          </h4>
          <div class="rto-history-table-wrap rto-history-table-wrap--compact">
            <table class="rto-history-table">
              <thead>
                <tr>
                  <th>Apelido</th>
                  <th>Tag</th>
                  <th>Mínimo</th>
                  <th>Máximo</th>
                  <th>Valor Simulado</th>
                  <th>Penalty</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(r, i) in details.restricoes" :key="i">
                  <td>{{ r.label || '—' }}</td>
                  <td class="mono">{{ r.tag }}</td>
                  <td class="mono">{{ formatNum(r.minimo) }}</td>
                  <td class="mono">{{ formatNum(r.maximo) }}</td>
                  <td class="mono">{{ formatNum(r.valorSimulado) }}</td>
                  <td class="mono">{{ formatNum(r.penaltyCalculado) }}</td>
                  <td>
                    <b-icon
                      v-if="r.status === 'acima_max' || r.status === 'abaixo_min'"
                      icon="exclamation-triangle-fill"
                      class="rto-history-restricao-warn"
                      :title="restrictionStatusTitle(r.status)"
                    />
                    <span v-else class="rto-history-chip" :class="restrictionStatusClass(r.status)">{{ restrictionStatusLabel(r.status) }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <!-- Downloads -->
        <section class="rto-history-section">
          <h4 class="rto-history-section-title">Downloads</h4>
          <div class="rto-history-downloads">
            <button
              class="rto-history-download-btn"
              :disabled="!details.xmlEnvioDisponivel || downloadInFlight"
              @click="downloadFile('xml-envio')"
            >
              <b-icon icon="file-earmark-code" class="mr-1" /> XML de Envio
            </button>
            <button
              class="rto-history-download-btn"
              :disabled="!details.xmlRetornoDisponivel || downloadInFlight"
              @click="downloadFile('xml-resultado')"
            >
              <b-icon icon="file-earmark-check" class="mr-1" /> XML de Resultado
            </button>
            <button
              class="rto-history-download-btn"
              :disabled="downloadInFlight"
              @click="downloadFile('json-resposta')"
            >
              <b-icon icon="file-earmark-text" class="mr-1" /> JSON Resposta
            </button>
          </div>
          <div v-if="downloadError" class="rto-history-download-error">
            <b-icon icon="exclamation-triangle" class="mr-1" /> {{ downloadError }}
          </div>
        </section>

        <!-- Raw JSON (collapsible) -->
        <section class="rto-history-section">
          <button class="rto-history-toggle-raw" @click="showRaw = !showRaw">
            <b-icon :icon="showRaw ? 'chevron-down' : 'chevron-right'" class="mr-1" />
            JSON Bruto
          </button>
          <pre v-if="showRaw" class="rto-history-raw">{{ JSON.stringify(details, null, 2) }}</pre>
        </section>
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios';
import { saveAs } from 'file-saver';
import { urlMainAPI } from '@/global';

export default {
  name: 'RtoHistory',

  data() {
    return {
      loading: false,
      error: null,
      items: [],
      filterTipo: null,
      filterOtimizacao: null,
      otimizacoesList: [],
      activeTab: 'todas',
      // Last optimization card
      lastDetails: null,
      lastDetailsLoading: false,
      lastDetailsError: null,
      lastDetailsId: null,
      // Details modal
      showDetails: false,
      detailsLoading: false,
      detailsError: null,
      details: null,
      detailsId: null,
      showRaw: false,
      downloadInFlight: false,
      downloadError: null,
      cancelInFlightId: null,
      cancelAllInFlight: false,
      // Diagnóstico (erros/avisos) collapse state
      showErroList: false,
      showErroRtoList: false,
    };
  },

  computed: {
    detailsTitle() {
      if (!this.details) return 'Detalhes';
      return `Otimização #${this.details.id} — ${this.details.otimizacaoNome || '—'}`;
    },

    parsedErro() {
      return this.parseDiagnostico(this.details && this.details.descricaoErro);
    },

    parsedErroRto() {
      return this.parseDiagnostico(this.details && this.details.rtoDescricaoErro);
    },

    parsedLastErro() {
      return this.parseDiagnostico(this.lastItem && this.lastItem.descricaoErro);
    },

    lastErroResumo() {
      const p = this.parsedLastErro;
      if (!p) return '';
      if (p.header) return p.header;
      return p.items.length ? p.items[0].texto : '';
    },

    statusGroups() {
      return {
        publicadas: [6, 7],
        comErro: [3, 4, 8, 9],
      };
    },

    itemsAfterOtimizacao() {
      if (this.filterOtimizacao == null) return this.items;
      return this.items.filter(i => String(i.otimizacaoId) === String(this.filterOtimizacao));
    },

    filteredItems() {
      const base = this.itemsAfterOtimizacao;
      if (this.activeTab === 'publicadas') {
        return base.filter(i => this.statusGroups.publicadas.includes(i.statusCodigo));
      }
      if (this.activeTab === 'comErro') {
        return base.filter(i => this.statusGroups.comErro.includes(i.statusCodigo));
      }
      return base;
    },

    tabCounts() {
      const base = this.itemsAfterOtimizacao;
      const publicadas = base.filter(i => this.statusGroups.publicadas.includes(i.statusCodigo)).length;
      const comErro = base.filter(i => this.statusGroups.comErro.includes(i.statusCodigo)).length;
      return { todas: base.length, publicadas, comErro };
    },

    emptyMessage() {
      if (this.activeTab === 'publicadas') return 'Sem otimizações publicadas.';
      if (this.activeTab === 'comErro') return 'Sem otimizações com erro.';
      return 'Nenhuma otimização registrada.';
    },

    runningStatusCodes() {
      // 0=NaoIniciado, 1=Pendente, 2=PendenteRetornoOtimizador
      return [0, 1, 2];
    },

    runningCount() {
      return this.items.filter(i => this.runningStatusCodes.includes(i.statusCodigo)).length;
    },

    lastItem() {
      return this.filteredItems.length > 0 ? this.filteredItems[0] : null;
    },

    lastCardAccentClass() {
      if (!this.lastItem) return '';
      const code = this.lastItem.statusCodigo;
      if (code === 6 || code === 7) return 'rto-last-card--ok';
      if (code === 3 || code === 4 || code === 8 || code === 9) return 'rto-last-card--erro';
      if (code === 10) return 'rto-last-card--neutro';
      return 'rto-last-card--pendente';
    },
  },

  watch: {
    lastItem(newVal, oldVal) {
      if (newVal?.id !== oldVal?.id) {
        this.loadLastDetails();
      }
    },
  },

  mounted() {
    this.loadOtimizacoes();
    this.load();
  },

  methods: {
    // Quebra a string crua de erro (paredao de "Warning: ... Warning: ... Erro: ...")
    // em um cabecalho (texto antes do 1o marcador) + lista de itens tipados.
    parseDiagnostico(raw) {
      if (!raw || typeof raw !== 'string') return null;
      const text = raw.trim();
      if (!text) return null;
      const parts = text.split(/(?=Warning:|Erro:)/g).map(s => s.trim()).filter(Boolean);
      let header = '';
      const items = [];
      parts.forEach(p => {
        if (/^Warning:/i.test(p)) items.push({ tipo: 'warning', texto: p.replace(/^Warning:\s*/i, '') });
        else if (/^Erro:/i.test(p)) items.push({ tipo: 'erro', texto: p.replace(/^Erro:\s*/i, '') });
        else header = header ? `${header} ${p}` : p;
      });
      return {
        header,
        items,
        warnings: items.filter(i => i.tipo === 'warning').length,
        erros: items.filter(i => i.tipo === 'erro').length,
        total: items.length,
      };
    },

    async loadOtimizacoes() {
      try {
        const res = await axios.get(`${urlMainAPI}/configurator/rto/listar`);
        const data = typeof res.data === 'string' ? JSON.parse(res.data) : res.data;
        const list = Array.isArray(data?.data) ? data.data : [];
        this.otimizacoesList = list
          .filter(o => o.status !== 'Deletado')
          .map(o => ({ id: o.id, nome: o.nome }));
      } catch (e) {
        console.error('Erro ao carregar lista de otimizações:', e);
        this.otimizacoesList = [];
      }
    },

    async load() {
      this.loading = true;
      this.error = null;
      try {
        const params = { limit: 100 };
        if (this.filterTipo !== null) params.tipo = this.filterTipo;
        const res = await axios.get(`${urlMainAPI}/configurator/rto/historico`, { params });
        const data = typeof res.data === 'string' ? JSON.parse(res.data) : res.data;
        this.items = Array.isArray(data?.items) ? data.items : [];
        this.loadLastDetails();
      } catch (e) {
        console.error('Erro ao carregar histórico RTO:', e);
        this.error = e?.response?.data?.message ?? e.message ?? 'Falha ao carregar histórico.';
      } finally {
        this.loading = false;
      }
    },

    async loadLastDetails() {
      const first = this.lastItem;
      if (!first) {
        this.lastDetails = null;
        this.lastDetailsId = null;
        return;
      }
      if (this.lastDetailsId === first.id && this.lastDetails) return;
      this.lastDetailsLoading = true;
      this.lastDetailsError = null;
      this.lastDetailsId = first.id;
      try {
        const res = await axios.get(`${urlMainAPI}/configurator/rto/historico/${first.id}`);
        const data = typeof res.data === 'string' ? JSON.parse(res.data) : res.data;
        if (data?.error) throw new Error(data.error);
        this.lastDetails = data;
      } catch (e) {
        console.error('Erro ao carregar detalhes da última otimização:', e);
        this.lastDetailsError = e?.message ?? 'Falha ao carregar detalhes.';
        this.lastDetails = null;
      } finally {
        this.lastDetailsLoading = false;
      }
    },

    async openDetails(id) {
      this.showDetails = true;
      this.detailsLoading = true;
      this.detailsError = null;
      this.details = null;
      this.detailsId = id;
      this.showRaw = false;
      this.downloadError = null;
      try {
        const res = await axios.get(`${urlMainAPI}/configurator/rto/historico/${id}`);
        const data = typeof res.data === 'string' ? JSON.parse(res.data) : res.data;
        if (data?.error) throw new Error(data.error);
        this.details = data;
      } catch (e) {
        console.error('Erro ao carregar detalhes RTO:', e);
        this.detailsError = e?.response?.data?.message ?? e.message ?? 'Falha ao carregar detalhes.';
      } finally {
        this.detailsLoading = false;
      }
    },

    isRunning(row) {
      return this.runningStatusCodes.includes(row.statusCodigo);
    },

    async cancelOne(id) {
      if (!confirm(`Cancelar a otimização #${id}?`)) return;
      this.cancelInFlightId = id;
      try {
        await axios.post(`${urlMainAPI}/configurator/rto/cancelar/${id}`);
        await this.load();
      } catch (e) {
        console.error('Erro ao cancelar otimização:', e);
        alert(`Erro ao cancelar: ${e?.response?.data?.message ?? e.message ?? 'desconhecido'}`);
      } finally {
        this.cancelInFlightId = null;
      }
    },

    async cancelAllRunning() {
      if (!confirm(`Cancelar TODAS as ${this.runningCount} otimização(ões) em execução? Esta ação é de emergência.`)) return;
      this.cancelAllInFlight = true;
      try {
        await axios.post(`${urlMainAPI}/configurator/rto/cancelar-em-execucao`);
        await this.load();
      } catch (e) {
        console.error('Erro ao cancelar todas:', e);
        alert(`Erro ao cancelar todas: ${e?.response?.data?.message ?? e.message ?? 'desconhecido'}`);
      } finally {
        this.cancelAllInFlight = false;
      }
    },

    async downloadFile(slug) {
      if (!this.details?.id) return;
      this.downloadInFlight = true;
      this.downloadError = null;
      try {
        const res = await axios.get(
          `${urlMainAPI}/configurator/rto/historico/${this.details.id}/${slug}`,
          { responseType: 'blob' }
        );
        const ext = slug === 'json-resposta' ? 'json' : 'xml';
        const suffix = slug === 'xml-envio' ? 'envio' : slug === 'xml-resultado' ? 'resultado' : 'resposta';
        const filename = `otimizacao_${this.details.id}_${suffix}.${ext}`;
        saveAs(res.data, filename);
      } catch (e) {
        console.error('Erro ao baixar arquivo:', e);
        this.downloadError = e?.response?.data?.message ?? e.message ?? 'Falha ao baixar arquivo.';
      } finally {
        this.downloadInFlight = false;
      }
    },

    formatModoShort(row) {
      if (!row) return '—';
      // Aceita ambos os shapes: { modo, modoCodigo } da listagem e { modoOtimizacao, modoOtimizacaoCodigo } do detalhe.
      const code = (row.modoCodigo ?? row.modoOtimizacaoCodigo ?? '').toString().toLowerCase();
      if (code === 'max') return 'MAX';
      if (code === 'min') return 'MIN';
      if (code === 'fixed' || code === 'fix') return 'FIX';
      const m = (row.modo || row.modoOtimizacao || '').toLowerCase();
      if (m.startsWith('max')) return 'MAX';
      if (m.startsWith('min')) return 'MIN';
      if (m.startsWith('fix') || m.startsWith('valor fixo')) return 'FIX';
      return row.modo || row.modoOtimizacao || '—';
    },

    modoBadgeClass(row) {
      const short = this.formatModoShort(row);
      if (short === 'MAX') return 'rto-history-modo--max';
      if (short === 'MIN') return 'rto-history-modo--min';
      if (short === 'FIX') return 'rto-history-modo--fix';
      return '';
    },

    computeAlvo(funcaoObjetivo, row) {
      if (!funcaoObjetivo) return null;
      const short = this.formatModoShort(row);
      if (short === 'MIN') return funcaoObjetivo.minimo;
      if (short === 'MAX') return funcaoObjetivo.maximo;
      // Fix ou desconhecido: usa fixedValue do row.meta (vem da listagem) ou o ponto médio
      if (row && row.meta != null) return row.meta;
      if (funcaoObjetivo.minimo != null && funcaoObjetivo.maximo != null) {
        return (funcaoObjetivo.minimo + funcaoObjetivo.maximo) / 2;
      }
      return null;
    },

    formatDesvio(funcaoObjetivo, row) {
      const alvo = this.computeAlvo(funcaoObjetivo, row);
      const sim = funcaoObjetivo?.valorSimulado;
      if (alvo == null || sim == null || isNaN(Number(alvo)) || isNaN(Number(sim))) return '—';
      const desvio = Number(sim) - Number(alvo);
      const sign = desvio > 0 ? '+' : '';
      const pct = Number(alvo) !== 0 ? ` (${sign}${((desvio / Math.abs(Number(alvo))) * 100).toFixed(2)}%)` : '';
      return `${sign}${this.formatNum(desvio)}${pct}`;
    },

    desvioClass(funcaoObjetivo, row) {
      const alvo = this.computeAlvo(funcaoObjetivo, row);
      const sim = funcaoObjetivo?.valorSimulado;
      if (alvo == null || sim == null) return '';
      const short = this.formatModoShort(row);
      const desvio = Number(sim) - Number(alvo);
      if (Math.abs(desvio) < 1e-9) return '';
      // MAX: simulado >= alvo = bom (positivo); MIN: simulado <= alvo = bom (negativo)
      if (short === 'MAX') return desvio >= 0 ? 'rto-history-variation--pos' : 'rto-history-variation--neg';
      if (short === 'MIN') return desvio <= 0 ? 'rto-history-variation--pos' : 'rto-history-variation--neg';
      return '';
    },

    formatDate(iso) {
      if (!iso) return '—';
      const d = new Date(iso);
      if (isNaN(d.getTime())) return '—';
      return d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'medium' });
    },

    formatRelativeTime(iso) {
      if (!iso) return '—';
      const d = new Date(iso);
      if (isNaN(d.getTime())) return '—';
      const diffSec = Math.round((Date.now() - d.getTime()) / 1000);
      if (diffSec < 0) return 'agora';
      if (diffSec < 60) return `há ${diffSec}s`;
      const diffMin = Math.round(diffSec / 60);
      if (diffMin < 60) return `há ${diffMin} min`;
      const diffHr = Math.round(diffMin / 60);
      if (diffHr < 24) return `há ${diffHr}h`;
      const diffDay = Math.round(diffHr / 24);
      if (diffDay < 30) return `há ${diffDay}d`;
      return d.toLocaleDateString('pt-BR');
    },

    formatDuration(minutes) {
      if (minutes == null || isNaN(Number(minutes))) return '—';
      const totalSec = Number(minutes) * 60;
      if (totalSec < 60) return `${totalSec.toFixed(1)}s`;
      const m = Math.floor(totalSec / 60);
      const s = Math.round(totalSec - m * 60);
      return `${m}min ${s}s`;
    },

    formatNum(val) {
      if (val == null || isNaN(Number(val))) return '—';
      return Number(val).toLocaleString('pt-BR', { maximumFractionDigits: 4 });
    },

    formatVariation(inicial, otimo) {
      if (inicial == null || otimo == null || Number(inicial) === 0) return '—';
      const pct = ((Number(otimo) - Number(inicial)) / Math.abs(Number(inicial))) * 100;
      const sign = pct > 0 ? '+' : '';
      return `${sign}${pct.toFixed(2)}%`;
    },

    variationClass(inicial, otimo) {
      if (inicial == null || otimo == null || Number(inicial) === 0) return '';
      const pct = (Number(otimo) - Number(inicial)) / Math.abs(Number(inicial));
      if (pct > 0.001) return 'rto-history-variation--pos';
      if (pct < -0.001) return 'rto-history-variation--neg';
      return '';
    },

    formatTipo(tipo) {
      // "Otimização Externa" e exibida apenas como "Otimização"
      if (tipo && /externa/i.test(tipo)) return 'Otimização';
      return tipo;
    },

    formatStatus(status) {
      // qualquer status de erro (ex.: "Erro no Primeiro/Segundo Retorno") e exibido apenas como "ERRO"
      if (status && /erro/i.test(status)) return 'ERRO';
      return status;
    },

    chipTipoClass(codigo) {
      // 0=Real, 1=Teste, 2=Otimizacao Externa, 3=RTO
      if (codigo === 0) return 'rto-history-chip--real';
      if (codigo === 1) return 'rto-history-chip--teste';
      if (codigo === 2) return 'rto-history-chip--externa';
      if (codigo === 3) return 'rto-history-chip--rto';
      return 'rto-history-chip--neutro';
    },

    chipStatusClass(codigo) {
      // 0=NaoIniciado, 1=Pendente, 2=PendenteRetornoOtimizador,
      // 3=ErroIdentificadoPrimeiroRetorno, 4=ErroIdentificadoSegundoRetorno,
      // 5=EmLegitimacao, 6=Finalizado, 7=OtimizacaoPublicada,
      // 8=NaoLegitimada, 9=ProcessoNaoEstavel, 10=Cancelada
      if (codigo === 6 || codigo === 7) return 'rto-history-chip--ok';
      if (codigo === 3 || codigo === 4 || codigo === 8 || codigo === 9) return 'rto-history-chip--erro';
      if (codigo === 10) return 'rto-history-chip--neutro';
      if (codigo === 0 || codigo === 1 || codigo === 2 || codigo === 5) return 'rto-history-chip--pendente';
      return 'rto-history-chip--neutro';
    },

    restrictionStatusClass(status) {
      if (status === 'ok') return 'rto-history-chip--ok';
      if (status === 'acima_max' || status === 'abaixo_min') return 'rto-history-chip--erro';
      return 'rto-history-chip--neutro';
    },

    restrictionStatusLabel(status) {
      const map = {
        ok: 'OK',
        acima_max: 'Acima máx',
        abaixo_min: 'Abaixo mín',
        sem_dado: 'Sem dado',
      };
      return map[status] ?? status;
    },

    restrictionStatusTitle(status) {
      const map = {
        acima_max: 'Acima do máximo',
        abaixo_min: 'Abaixo do mínimo',
      };
      return map[status] ?? this.restrictionStatusLabel(status);
    },
  },
};
</script>

<style scoped>
.rto-history {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 16px 24px;
  background-color: #f9fafb;
  overflow: hidden;
}

.rto-history-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 12px;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 12px;
  flex-shrink: 0;
}

.rto-history-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.rto-history-title-icon {
  font-size: 1.4rem;
  color: #ff5f00;
}

.rto-history-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.rto-history-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 20px;
  padding: 0 8px;
  background-color: #ff5f00;
  color: white;
  font-size: 0.7rem;
  font-weight: 700;
  border-radius: 10px;
}

.rto-history-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.rto-history-filter {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.rto-history-filter-label {
  font-size: 0.6rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0;
}

.rto-history-select {
  font-size: 0.78rem;
  padding: 4px 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background-color: white;
  color: #374151;
  min-width: 180px;
}

.rto-history-refresh-btn {
  display: flex;
  align-items: center;
  padding: 6px 14px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background-color: white;
  color: #374151;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 150ms, border-color 150ms;
  align-self: flex-end;
}

.rto-history-refresh-btn:hover:not(:disabled) {
  background-color: #fff7ed;
  border-color: #ff5f00;
  color: #ff5f00;
}

.rto-history-refresh-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Tabs */
.rto-history-tabs {
  display: flex;
  align-items: center;
  gap: 4px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 12px;
  flex-shrink: 0;
}

.rto-history-tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  border-radius: 0;
  margin-bottom: -9px;
  font-size: 0.78rem;
  font-weight: 600;
  color: #6b7280;
  cursor: pointer;
  transition: color 150ms, border-color 150ms;
}

.rto-history-tab:hover {
  color: #374151;
}

.rto-history-tab--active {
  color: #ff5f00;
  border-bottom-color: #ff5f00;
}

.rto-history-tab-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 18px;
  padding: 0 6px;
  background-color: #e5e7eb;
  color: #6b7280;
  font-size: 0.65rem;
  font-weight: 700;
  border-radius: 9px;
  transition: background-color 150ms, color 150ms;
}

.rto-history-tab--active .rto-history-tab-count {
  background-color: #ff5f00;
  color: white;
}

.rto-history-tab-count--ok { background-color: #dcfce7; color: #15803d; }
.rto-history-tab-count--erro { background-color: #fee2e2; color: #b91c1c; }

.rto-history-tab--active .rto-history-tab-count--ok,
.rto-history-tab--active .rto-history-tab-count--erro {
  background-color: #ff5f00;
  color: white;
}

/* Last optimization card */
.rto-last-card {
  background-color: white;
  border: 1px solid #e5e7eb;
  border-left: 4px solid #ff5f00;
  border-radius: 6px;
  padding: 12px 16px;
  margin-bottom: 12px;
  flex-shrink: 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.rto-last-card--ok { border-left-color: #15803d; }
.rto-last-card--erro { border-left-color: #b91c1c; }
.rto-last-card--neutro { border-left-color: #6b7280; }
.rto-last-card--pendente { border-left-color: #f59e0b; }

.rto-last-card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 10px;
  border-bottom: 1px solid #f3f4f6;
  margin-bottom: 10px;
}

.rto-last-card-eyebrow {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #6b7280;
}

.rto-last-card-time {
  margin-left: auto;
  font-size: 0.72rem;
  color: #6b7280;
  font-style: italic;
}

.rto-last-card-details-btn {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  background-color: transparent;
  border: 1px solid #ff5f00;
  border-radius: 4px;
  color: #ff5f00;
  font-size: 0.7rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 150ms;
}

.rto-last-card-details-btn:hover {
  background-color: #fff7ed;
}

.rto-last-card-body {
  display: grid;
  grid-template-columns: 1.2fr 1.4fr 1.4fr;
  gap: 24px;
}

.rto-last-card-col {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.rto-last-card-title {
  font-size: 1rem;
  font-weight: 700;
  color: #1f2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rto-last-card-subtle {
  font-size: 0.72rem;
  color: #6b7280;
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  align-items: center;
}

.rto-last-card-sep {
  color: #d1d5db;
}

.rto-last-card-error {
  font-size: 0.7rem;
  color: #b91c1c;
  margin-top: 4px;
}

.rto-last-card-error-head {
  display: flex;
  align-items: flex-start;
  gap: 2px;
}

.rto-last-card-error-icon {
  flex-shrink: 0;
  margin-top: 2px;
}

.rto-last-card-error-text {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;
}

.rto-last-card-error-count {
  display: inline-block;
  margin-top: 5px;
  padding: 3px 9px;
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 4px;
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #b91c1c;
  cursor: pointer;
  transition: background-color 0.15s;
}

.rto-last-card-error-count:hover {
  background-color: #fee2e2;
  border-color: #fca5a5;
}

.rto-last-card-label {
  font-size: 0.6rem;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 2px;
}

.rto-last-card-label-inline {
  font-size: 0.68rem;
  color: #6b7280;
  font-weight: 500;
}

.rto-last-card-loading {
  padding: 4px 0;
  color: #9ca3af;
}

.rto-last-card-objective {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.rto-last-card-objective-tag {
  font-size: 0.72rem;
  color: #374151;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rto-last-card-objective-row {
  display: flex;
  gap: 6px;
  align-items: baseline;
}

.rto-last-card-objective-value {
  font-size: 0.85rem;
  font-weight: 700;
  color: #ff5f00;
}

.rto-last-card-resumo {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.rto-last-card-resumo-row {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.rto-last-card-resumo-chips {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.rto-history-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 60px 20px;
  color: #6b7280;
  font-size: 0.85rem;
}

.rto-history-state--error {
  color: #b91c1c;
}

.rto-history-state .b-icon {
  font-size: 2rem;
}

/* Table */
.rto-history-table-wrap {
  flex: 1;
  overflow: auto;
  background-color: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
}

.rto-history-table-wrap--compact {
  max-height: 320px;
  flex: none;
}

.rto-history-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.78rem;
}

.rto-history-table thead th {
  position: sticky;
  top: 0;
  background-color: #f3f4f6;
  color: #6b7280;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 8px 12px;
  text-align: left;
  border-bottom: 1px solid #e5e7eb;
  z-index: 1;
}

.rto-history-table tbody tr {
  border-bottom: 1px solid #f3f4f6;
  transition: background-color 100ms;
}

.rto-history-table tbody tr:hover {
  background-color: #f9fafb;
}

.rto-history-table tbody td {
  padding: 8px 12px;
  color: #374151;
  vertical-align: middle;
}

.rto-history-td-mono,
.mono {
  font-family: 'JetBrains Mono', 'SF Mono', Consolas, monospace;
  font-size: 0.72rem;
}

.rto-history-td-truncate {
  max-width: 260px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rto-history-details-btn {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  background-color: #ff5f00;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 150ms;
}

.rto-history-details-btn:hover {
  background-color: #e04e00;
}

.rto-history-row-actions {
  display: flex;
  gap: 4px;
  align-items: center;
}

.rto-history-cancel-row-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 6px;
  background-color: transparent;
  color: #b91c1c;
  border: 1px solid #fecaca;
  border-radius: 4px;
  font-size: 0.85rem;
  cursor: pointer;
  transition: background-color 150ms, color 150ms, border-color 150ms;
}

.rto-history-cancel-row-btn:hover:not(:disabled) {
  background-color: #fee2e2;
  color: #991b1b;
  border-color: #fca5a5;
}

.rto-history-cancel-row-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.rto-history-cancel-all-btn {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  background-color: #b91c1c;
  color: white;
  border: 1px solid #b91c1c;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 150ms;
  align-self: flex-end;
}

.rto-history-cancel-all-btn:hover:not(:disabled) {
  background-color: #991b1b;
}

.rto-history-cancel-all-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Chips */
.rto-history-chip {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
}

.rto-history-chip--ok { background-color: #dcfce7; color: #15803d; }
.rto-history-chip--erro { background-color: #fee2e2; color: #b91c1c; }
.rto-history-chip--neutro { background-color: #e5e7eb; color: #6b7280; }
.rto-history-chip--pendente { background-color: #fef3c7; color: #92400e; }
.rto-history-chip--atencao { background-color: #fef3c7; color: #92400e; }

.rto-history-restricao-warn { color: #f59e0b; font-size: 1.05rem; }

.rto-history-chip--real { background-color: #dbeafe; color: #1d4ed8; }
.rto-history-chip--teste { background-color: #e9d5ff; color: #7e22ce; }
.rto-history-chip--externa { background-color: #dcfce7; color: #15803d; }
.rto-history-chip--rto { background-color: #ffedd5; color: #c2410c; }

/* Details modal sections */
.rto-history-details {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.rto-history-section {
  background-color: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 12px 16px;
}

.rto-history-section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
  margin: 0 0 10px 0;
  padding-bottom: 6px;
  border-bottom: 1px solid #e5e7eb;
}

.rto-history-section-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 18px;
  padding: 0 6px;
  background-color: #6b7280;
  color: white;
  font-size: 0.65rem;
  font-weight: 700;
  border-radius: 9px;
}

.rto-history-resumo {
  margin-left: auto;
  display: flex;
  gap: 4px;
}

.rto-history-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 10px 16px;
}

.rto-history-grid > div {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.rto-history-grid label {
  font-size: 0.6rem;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin: 0;
}

.rto-history-grid span {
  font-size: 0.78rem;
  color: #374151;
}

.rto-history-error {
  color: #b91c1c;
  font-weight: 500;
}

/* Diagnóstico — erros/avisos divididos e colapsáveis */
.rto-diag {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.rto-diag-label {
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
  font-weight: 700;
  margin-bottom: 4px;
}

.rto-diag-header {
  color: #b91c1c;
  font-weight: 600;
  font-size: 0.82rem;
  line-height: 1.4;
  margin: 0 0 8px;
}

.rto-diag-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  color: #b91c1c;
  border-radius: 5px;
  padding: 5px 12px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s;
}

.rto-diag-toggle:hover {
  background-color: #fee2e2;
}

.rto-diag-toggle-hint {
  margin-left: 6px;
  font-weight: 500;
  font-size: 0.7rem;
  opacity: 0.7;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.rto-diag-list {
  list-style: none;
  margin: 8px 0 0;
  padding: 0;
  max-height: 320px;
  overflow-y: auto;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
}

.rto-diag-item {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  padding: 8px 10px;
  font-size: 0.78rem;
  line-height: 1.45;
  border-bottom: 1px solid #f3f4f6;
}

.rto-diag-item:last-child {
  border-bottom: none;
}

.rto-diag-item--warning {
  background-color: #fffbeb;
  color: #92400e;
}

.rto-diag-item--erro {
  background-color: #fef2f2;
  color: #b91c1c;
  font-weight: 500;
}

.rto-diag-item-icon {
  flex-shrink: 0;
  margin-top: 2px;
}

.rto-history-variation--pos { color: #15803d; font-weight: 600; }
.rto-history-variation--neg { color: #b91c1c; font-weight: 600; }

/* Modo badge (MAX/MIN/FIX) */
.rto-history-modo {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  font-family: 'JetBrains Mono', 'SF Mono', Consolas, monospace;
}

.rto-history-modo--max { background-color: #dcfce7; color: #15803d; }
.rto-history-modo--min { background-color: #ffedd5; color: #c2410c; }
.rto-history-modo--fix { background-color: #dbeafe; color: #1d4ed8; }

/* Downloads */
.rto-history-downloads {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.rto-history-download-btn {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  background-color: white;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  color: #374151;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 150ms, border-color 150ms, color 150ms;
}

.rto-history-download-btn:hover:not(:disabled) {
  background-color: #fff7ed;
  border-color: #ff5f00;
  color: #ff5f00;
}

.rto-history-download-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.rto-history-download-error {
  margin-top: 8px;
  padding: 6px 10px;
  background-color: #fee2e2;
  color: #b91c1c;
  border-radius: 4px;
  font-size: 0.72rem;
}

.rto-history-toggle-raw {
  display: flex;
  align-items: center;
  background: none;
  border: none;
  padding: 4px 0;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
  cursor: pointer;
}

.rto-history-toggle-raw:hover {
  color: #ff5f00;
}

.rto-history-raw {
  margin-top: 8px;
  padding: 12px;
  background-color: #1f2937;
  color: #d1d5db;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.7rem;
  border-radius: 4px;
  max-height: 400px;
  overflow: auto;
  white-space: pre;
}
</style>
